import { Badge, DataTable, GroupByPanel, Modal } from "@aidkitorg/component-library";
import { FieldOption, GroupByConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { DocumentLibraryItem } from "@aidkitorg/types/lib/convoai/documentLibrary.types";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { usePost } from "../../API";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { snakeToEnglish } from "../../Util";
import { PreviewDocument } from "./DocumentLibrary";


export function DocumentEvalOverview() {

  type DocumentEvaluation = NonNullable<Awaited<ReturnType<typeof getDocumentScores>>['data']>[number];

  const getDocumentScores = usePost('/ai/document_library/scores');
  const [evaluations, setEvaluations] = useState<DocumentEvaluation[]>([]);
  const [previewDocument, setPreviewDocument] = useState<DocumentLibraryItem | null>(null);

  useAsyncEffect(async () => {

    const scores = await getDocumentScores({});

    if (!scores.data || scores.error) {
      return;
    }

    setEvaluations(scores.data);

  }, []);

  function determineLatencyBand(millis: number) {
    if (millis < 1_000) {
      return 'Very Low';
    }
    if (millis < 4_000) {
      return 'Low';
    }
    if (millis < 10_000) {
      return 'Medium';
    }
    if (millis < 60_000) {
      return 'High';
    }
    
    return 'Very High';
  }

  function ListCell<T>({ items, render, keyFn, legend, showCount }: T extends string ? {
    items: T[],
    render?: (t: T) => React.ReactNode
    keyFn?: (t: T) => string,
    legend?: React.ReactNode,
    showCount?: number
  } : {
    items: T[],
    render: (t: T) => React.ReactNode,
    keyFn: (t: T) => string,
    legend?: React.ReactNode,
    showCount?: number
  }) {

    const [useShowCount, setUseShowCount] = useState<boolean>(typeof showCount === 'number');

    if (!items.length) {
      return <span className="text-gray-400">None</span>
    }

    return <div
      onMouseOver={() => setUseShowCount(false)}
      onMouseLeave={() => setUseShowCount(true)}
    >
      <ul className="space-y-1 list-inside border rounded-md p-2">
        <li hidden={!legend} className="border-b border-gray-300 px-0.5 text-center">{legend}</li>
        {
          (useShowCount ? items.slice(0, showCount) : items).map(item =>
            <li key={keyFn?.(item) ?? item} className="">{render?.(item) ?? item}</li>
          )
        }
        <li hidden={!useShowCount || (!showCount || showCount >= items.length)}>
          <span>{items.length - (showCount || 0)} more</span>
        </li>
      </ul>
    </div>
  }

  const fieldToStrFn = ({ name }: DocumentEvaluation['fieldResults'][number]) => snakeToEnglish(name);
  const numFormatter = new Intl.NumberFormat(undefined, {
    unit: 'millisecond',
    compactDisplay: 'long',
    useGrouping: true
  });

  const columnDefs: ColumnDef<DocumentEvaluation>[] = useMemo(() => [
    {
      id: 'docId',
      header: 'Doc ID',
      accessorKey: 'document.id'
    },
    {
      id: 'category',
      header: 'Category',
      accessorKey: 'document.category',
      cell: ({ row, renderValue }) => {

        if (row.getIsGrouped()) {
          return <div>
            {renderValue() as any}
            GROUPED
          </div>
        }

        return renderValue();
      }
    },
    {
      id: 'valid',
      header: 'Valid',
      accessorFn: ({ document: { valid } }) => valid ? 'Valid' : 'Invalid',
      cell: ({ row: { original: { document } } }) => <Badge variant={document.valid ? 'success' : 'error'}>{document.valid ? 'Valid' : 'Invalid'}</Badge>
    },
    {
      id: 'variations',
      header: 'Attributes',
      cell: ({ row: { original: { document } } }) =>
        <ListCell
          render={variations => <span className="text-xs">{variations}</span>}
          items={document.metadata?.variations ?? []}
          showCount={1}
        />
    },
    {
      id: 'correct',
      header: 'Correct',
      cell: ({ row: { original: { fieldResults } } }) =>
        <ListCell
          showCount={3}
          keyFn={fieldToStrFn}
          render={field => <span className="text-xs">{field.name}</span>}
          items={fieldResults
            .filter(result => result.actual === result.expected)}
        />
    },
    {
      id: 'errors',
      header: 'Errors',
      accessorFn: ({ fieldResults }) => fieldResults.filter(({ expected, actual }) => expected !== actual).length,
      cell: ({ row: { original: { fieldResults } } }) =>
        <ListCell
          showCount={3}
          legend={<span className="text-xs text-gray-400">{'Document <-> Response'}</span>}
          keyFn={fieldToStrFn}
          render={({ name, expected, actual }) =>
            <span className="text-xs"><strong>{snakeToEnglish(name)}</strong>:&nbsp;"{expected ?? ''}" != "{actual ?? ''}"</span>}
          items={fieldResults
            .filter(result => result.actual !== result.expected)}
        />
    },
    {
      id: 'template',
      header: 'Template Version',
      enableResizing: true,
      accessorFn: ({ template }) => `${template.name} v${template.version ?? 0}`
    },
    {
      id: 'model',
      accessorFn: ({ model: { name, params } }) => params?.reasoning ? `${name}-${params.reasoning}` : name,
      header: 'LLM Version',
    },
    {
      id: 'passed',
      header: 'Pass/Fail',
      accessorFn: ({ passed }) => passed ? 'Pass' : 'Fail',
      cell: ({ row: { original: { passed } } }) => <Badge variant={passed ? 'success' : 'error'}>{passed ? 'Pass' : 'Fail'}</Badge>
    },
    {
      id: 'latency',
      header: 'Latency',
      accessorKey: 'requestDurationMillis',
      cell: ({ row: { original: { requestDurationMillis } } }) => <span>{numFormatter.format(requestDurationMillis)}&nbsp;ms</span>
    },
    {
      id: 'latencyBand',
      header: 'Latency Band',
      accessorFn: ({ requestDurationMillis }) => determineLatencyBand(requestDurationMillis),
      cell: ({ row: { original: { requestDurationMillis } } }) => <span>{determineLatencyBand(requestDurationMillis)}</span>
    }
  ], []);

  const fieldOptions = useMemo<FieldOption[]>(() => [
    {
      label: 'Model',
      key: 'model',
      type: 'text'
    },
    {
      label: 'Validity',
      key: 'valid',
      type: 'boolean'
    },
    {
      label: 'Template',
      key: 'template',
      type: 'text'
    },
    {
      label: 'Category',
      key: 'category',
      type: 'text'
    },
    {
      label: 'Pass/Fail',
      key: 'passed',
      type: 'boolean'
    },
    {
      label: 'Latency Band',
      key: 'latencyBand',
      type: 'number'
    }
  ], []);

  const [groupByConfig, setGroupByConfig] = useState<GroupByConfig>({
    enabled: true,
    rules: []
  });

  // Columns the user has explicitly hidden via the table's column-visibility control.
  // Seeded with the columns we hide by default.
  const [userHiddenCols, setUserHiddenCols] = useState<string[]>(['docId', 'correct']);

  // A grouped column renders as a group header, so we also hide it from the row body.
  const groupedCols = useMemo(
    () => groupByConfig.rules?.map(({ field }) => field) ?? [],
    [groupByConfig]
  );

  const hiddenFields = useMemo(
    () => Array.from(new Set([...userHiddenCols, ...groupedCols])),
    [userHiddenCols, groupedCols]
  );

  const handleColumnVisibilityChange = useCallback((visibility: Record<string, boolean>) => {
    const grouped = new Set(groupedCols);
    // Track only the user's explicit hides; grouping-driven hides are derived
    // separately so a column reappears once it's no longer grouped.
    const nextHidden = Object.entries(visibility)
      .filter(([id, visible]) => !visible && !grouped.has(id))
      .map(([id]) => id);
    setUserHiddenCols(nextHidden);
  }, [groupedCols]);

  return <div className="flex-col p-4">
    <h4 className="mb-2">Document Score Review</h4>
    <div className="overflow-x-auto px-1 pt-2 pb-1">
      <DataTable
        resizableColumns
        enableColumnReordering
        enableColumnVisibility
        groupByControl={
          <GroupByPanel
            availableFields={fieldOptions}
            value={groupByConfig}
            onConfirm={setGroupByConfig}
          />
        }
        groupBy={groupByConfig}
        columns={columnDefs}
        data={evaluations}
        hiddenCols={hiddenFields}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        actions={[
          {
            label: 'Preview',
            onClick(item) {
              setPreviewDocument(item.document);
            }
          },
        ]}
      />
    </div>
    {/* Preview Modal */}
    {previewDocument && <Modal
      open={!!previewDocument}
      onClose={() => setPreviewDocument(null)}
      title={`Document Preview - ${previewDocument?.original_filename}`}
      size="lg"
    >
      <PreviewDocument
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </Modal>}
  </div>
}
